const TAU = Math.PI * 2;
const BUCKETS = 32;
const SHADE_BIAS = 1.2;

export const STILL_TIME = 3.1;

export function rgbToHsl([r, g, b]) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l * 100];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    return [h * 60, s * 100, l * 100];
}

const hsl = ([h, s, l]) => `hsl(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(1)}%)`;
const hsla = ([h, s, l], a) => `hsla(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(1)}%, ${a})`;

function makeRamp(stops) {
    const segments = stops.length - 1;
    const line = [];
    const dot = [];
    const lineRaw = [];
    const dotRaw = [];
    for (let i = 0; i < BUCKETS; i += 1) {
        const position = (i / (BUCKETS - 1)) * segments;
        const from = Math.min(segments - 1, Math.floor(position));
        const mix = position - from;
        const lv = stops[from].map((v, k) => v + (stops[from + 1][k] - v) * mix);
        const dv = [lv[0], lv[1], Math.min(80, lv[2] + 6)];
        line.push(hsl(lv));
        dot.push(hsl(dv));
        lineRaw.push(lv);
        dotRaw.push(dv);
    }
    return { line, dot, lineRaw, dotRaw };
}

export function buildPalette(midRgb, deepRgb, accentRgb = null) {
    const mid = rgbToHsl(midRgb);
    const [deepHue, deepSat, deepLight] = rgbToHsl(deepRgb);
    const deep = [deepHue, Math.min(100, deepSat + 16), deepLight + 4];
    const light = [mid[0] - 12, Math.min(100, mid[1] + 16), Math.min(78, mid[2] + 9)];

    const blueRamp = makeRamp([light, mid, deep]);
    const palette = { line: blueRamp.line, dot: blueRamp.dot, bodyFrom: hsla(light, 0.14), bodyTo: hsla(mid, 0.07) };

    if (accentRgb) {
        const acc = rgbToHsl(accentRgb);
        const pink = [acc[0], 100, Math.min(80, acc[2] + 8)];
        const pinkMid = [acc[0] + (mid[0] - acc[0]) * 0.5, 88, (acc[2] + mid[2]) / 2 + 4];
        const pinkRamp = makeRamp([pink, pinkMid, deep]);
        palette.pinkLine = pinkRamp.line;
        palette.pinkDot = pinkRamp.dot;
        palette.blueLineRaw = blueRamp.lineRaw;
        palette.blueDotRaw = blueRamp.dotRaw;
        palette.pinkLineRaw = pinkRamp.lineRaw;
        palette.pinkDotRaw = pinkRamp.dotRaw;
    }

    return palette;
}

export const DEFAULT_PALETTE = buildPalette([37, 99, 235], [30, 58, 138]);

const seeded = (() => {
    let state = 29;
    return () => {
        state = (state * 16807) % 2147483647;
        return state / 2147483647;
    };
})();

const DOTS = Array.from({ length: 170 }, () => {
    const near = seeded();
    return {
        theta: Math.acos(2 * seeded() - 1),
        phi: seeded() * TAU,
        radius: 1.03 + near * near * 0.34,
        size: 0.4 + seeded() * 0.8,
        drift: 0.08 + seeded() * 0.3,
        alpha: 0.35 + seeded() * 0.55,
        phase: seeded() * TAU,
    };
});

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const NO_LEAN = [0, 0];

function rotate(x, y, z, spin, tilt, roll) {
    const cs = Math.cos(spin);
    const sn = Math.sin(spin);
    const x1 = x * cs + z * sn;
    const z1 = -x * sn + z * cs;
    const ct = Math.cos(tilt);
    const st = Math.sin(tilt);
    const y2 = y * ct - z1 * st;
    const z2 = y * st + z1 * ct;
    const cr = Math.cos(roll);
    const sr = Math.sin(roll);
    return [x1 * cr - y2 * sr, x1 * sr + y2 * cr, z2];
}

function vertex(theta, phi, t, amp, view) {
    const flow = Math.sin(theta);
    const th = theta + 0.16 * flow * Math.sin(2 * phi + 1.3 * theta + t * 0.7);
    const ph = phi + 0.22 * flow * Math.sin(3 * theta - phi + t * 0.5);
    const ring = Math.sin(th);
    const ux = ring * Math.cos(ph);
    const uy = Math.cos(th);
    const uz = ring * Math.sin(ph);
    const wave = Math.sin(2.3 * ux + 1.6 * uy + t * 1.5)
        + Math.sin(2.7 * uz - 2.1 * ux - t * 1.1)
        + Math.sin(2.1 * uy + 1.9 * uz + t * 0.8)
        + 0.7 * Math.sin(5.3 * ux + 4.1 * uz - 3.7 * uy + t * 2.1)
        + 0.5 * Math.sin(7.7 * uy - 6.1 * uz + 4.9 * ux - t * 2.6);
    const r = 1 + amp * 0.032 * wave;
    return rotate(ux * r, uy * r, uz * r, view.spin, view.tilt, view.roll);
}

const bucketFor = (x, y) => (
    Math.round(Math.pow(clamp(0.5 + 0.5 * (x * 0.78 - y * 0.78), 0, 1), SHADE_BIAS) * (BUCKETS - 1))
);

export function drawOrb(ctx, px, t, amp = 1, lean = NO_LEAN, palette = DEFAULT_PALETTE) {
    ctx.clearRect(0, 0, px, px);

    const center = px / 2;
    const radius = px * 0.37;
    const lat = clamp(Math.round(px / 3.3), 9, 56);
    const lon = clamp(Math.round(px / 1.9), 14, 110);
    const view = {
        spin: t * 0.3 + lean[0] * 0.6,
        tilt: 0.5 + lean[1] * 0.4,
        roll: 0.7 + t * 0.12,
    };

    const grid = [];
    for (let i = 0; i <= lat; i += 1) {
        const row = [];
        for (let j = 0; j <= lon; j += 1) {
            row.push(vertex((i / lat) * Math.PI, (j / lon) * TAU, t, amp, view));
        }
        grid.push(row);
    }

    const body = ctx.createRadialGradient(center - radius * 0.3, center - radius * 0.3, radius * 0.1, center, center, radius * 1.15);
    body.addColorStop(0, palette.bodyFrom);
    body.addColorStop(1, palette.bodyTo);
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(center, center, radius * 1.04, 0, TAU);
    ctx.fill();

    const base = clamp(px / 120, 0.5, 1);
    ctx.lineCap = 'round';
    const noise3 = (x, y, z) => (
        Math.sin(x * 5.3 + y * 8.1 + z * 6.7 + t * 0.11)
        * Math.sin(y * 7.9 - z * 4.3 + t * 0.07)
    );
    const smoothBlend = (w, bRaw, pRaw) => hsl(bRaw.map((v, k) => v + (pRaw[k] - v) * w));

    const pickLine = palette.pinkLine
        ? (mx, my, mz) => {
            const bucket = bucketFor(mx, my);
            const w = clamp((noise3(mx, my, mz) + 0.85) / 0.55, 0, 1);
            if (w <= 0.02) return palette.line[bucket];
            if (w >= 0.98) return palette.pinkLine[bucket];
            return smoothBlend(w, palette.blueLineRaw[bucket], palette.pinkLineRaw[bucket]);
        }
        : (mx, my) => palette.line[bucketFor(mx, my)];

    const pickDot = palette.pinkDot
        ? (x, y, z) => {
            const bucket = bucketFor(x, y);
            const w = clamp((noise3(x, y, z) + 0.85) / 0.55, 0, 1);
            if (w <= 0.02) return palette.dot[bucket];
            if (w >= 0.98) return palette.pinkDot[bucket];
            return smoothBlend(w, palette.blueDotRaw[bucket], palette.pinkDotRaw[bucket]);
        }
        : (x, y) => palette.dot[bucketFor(x, y)];

    const segment = (a, b, latitude) => {
        const mx = (a[0] + b[0]) / 2;
        const my = (a[1] + b[1]) / 2;
        const mz = (a[2] + b[2]) / 2;
        const z = mz;
        const edge = 1 - Math.min(1, Math.abs(z));
        const rim = Math.pow(edge, 1.25);
        const poleFade = Math.pow(clamp(Math.sin(latitude * Math.PI) / 0.85, 0.05, 1), 1.5);
        const alpha = (z > 0 ? 0.26 + 0.64 * rim : 0.1 + 0.34 * rim) * poleFade;
        ctx.strokeStyle = pickLine(mx, my, mz);
        ctx.beginPath();
        ctx.moveTo(center + a[0] * radius, center - a[1] * radius);
        ctx.lineTo(center + b[0] * radius, center - b[1] * radius);
        if (edge > 0.55) {
            ctx.lineWidth = base * 3.4;
            ctx.globalAlpha = 0.05 + 0.07 * rim;
            ctx.stroke();
        }
        ctx.lineWidth = base;
        ctx.globalAlpha = alpha;
        ctx.stroke();
    };

    for (let i = 1; i < lat; i += 1) {
        for (let j = 0; j < lon; j += 1) segment(grid[i][j], grid[i][j + 1], i / lat);
    }
    for (let j = 0; j < lon; j += 1) {
        for (let i = 0; i < lat; i += 1) segment(grid[i][j], grid[i + 1][j], (i + 0.5) / lat);
    }

    const dots = Math.min(DOTS.length, Math.round(px * 1.4));
    for (let k = 0; k < dots; k += 1) {
        const d = DOTS[k];
        const phi = d.phi + t * d.drift;
        const ring = Math.sin(d.theta);
        const reach = d.radius * (1 + 0.025 * Math.sin(t * 1.7 + d.phase));
        const [x, y, z] = rotate(
            reach * ring * Math.cos(phi),
            reach * Math.cos(d.theta),
            reach * ring * Math.sin(phi),
            view.spin,
            view.tilt,
            view.roll,
        );
        ctx.globalAlpha = d.alpha * (z > 0 ? 1 : 0.45);
        ctx.fillStyle = pickDot(x, y, z);
        ctx.beginPath();
        ctx.arc(center + x * radius, center - y * radius, Math.max(0.4, d.size * clamp(px / 120, 0.5, 1)), 0, TAU);
        ctx.fill();
    }
    ctx.globalAlpha = 1;
}
